import { Grn } from '../../types/grn';
import { Edit, Check, Trash2, Package, Eye } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyUtils';
import DeleteConfirmationModal from '../modals/DeleteConfirmationModal';
import { useState, useEffect } from 'react';
import { getPoItems } from '../../services/grnApi';

interface GrnTableProps {
  grns: Grn[];
  onEdit: (grn: Grn) => void;
  onFinalize: (grn: Grn) => void;
  onDelete: (grn: Grn) => void;
  onView: (grn: Grn) => void;
  onCreateNew: () => void;
  showCreateButton?: boolean;
  showActions?: boolean;
  jobCardId?: number;
  isAdmin?: boolean;
}

/**
 * Reusable GRN table component that displays a list of Goods Received Notes
 * with actions for editing, finalizing, and deleting (for non-finalized GRNs)
 */
export default function GrnTable({
  grns,
  onEdit,
  onFinalize,
  onDelete,
  onView,
  onCreateNew,
  showCreateButton = true,
  showActions = true,
  jobCardId,
  isAdmin = false
}: GrnTableProps) {
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    grn: Grn | null;
  }>({
    isOpen: false,
    grn: null
  });

  const [poItems, setPoItems] = useState<any[]>([]);
  const [isLoadingPoItems, setIsLoadingPoItems] = useState(false);

  // Load PO items to calculate proper balance
  useEffect(() => {
    if (jobCardId) {
      const loadPoItems = async () => {
        try {
          setIsLoadingPoItems(true);
          const items = await getPoItems(jobCardId);
          setPoItems(items);
        } catch (error) {
          console.error('Error loading PO items:', error);
          setPoItems([]);
        } finally {
          setIsLoadingPoItems(false);
        }
      };
      loadPoItems();
    }
  }, [jobCardId]);

  const handleDeleteClick = (grn: Grn) => {
    setDeleteModal({
      isOpen: true,
      grn
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.grn) {
      onDelete(deleteModal.grn);
      setDeleteModal({
        isOpen: false,
        grn: null
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      grn: null
    });
  };

  // Format datetime for display
  const formatDateTime = (date: string | null, time: string | null) => {
    if (!date) return '—';
    
    try {
      const dateObj = new Date(date);
      const formattedDate = dateObj.toLocaleDateString();
      const formattedTime = time ? time.slice(0, 5) : dateObj.toLocaleTimeString().slice(0, 5);
      return `${formattedDate} ${formattedTime}`;
    } catch (error) {
      return '—';
    }
  };

  // Calculate total balance of items not yet received
  const calculateTotalBalance = () => {
    if (isLoadingPoItems || poItems.length === 0) {
      return 0; // Show 0 while loading or if no PO items
    }

    // Calculate total remaining quantities from PO items
    // The PO items already have remaining_qty calculated by the backend
    const totalRemaining = poItems.reduce((total, item) => {
      const remainingQty = parseInt(String(item.remaining_qty)) || 0;
      return total + remainingQty;
    }, 0);

    return totalRemaining;
  };
  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Goods Received Notes</h2>
        {showCreateButton && isAdmin && (
          <button
            onClick={onCreateNew}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 transition-colors duration-200"
          >
            <Package className="w-4 h-4 mr-2" />
            GRN
          </button>
        )}
      </div>
      
      {grns.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">
          {isAdmin ? 'No GRNs yet.' : 'No GRNs available to view.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">GRN #</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Received</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Received By</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Totals</th>
                {showActions && isAdmin && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {grns.map((grn) => (
                <tr key={grn.id}>
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{grn.grn_number || '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${grn.status === 'finalized' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'}`}>
                      {grn.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                    {formatDateTime(grn.received_date || null, grn.received_time || null)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{grn.received_by || '—'}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                    Net: {formatCurrency(grn.totals_net)} | 
                    VAT: {formatCurrency(grn.totals_vat)} | 
                    Gross: {formatCurrency(grn.totals_gross)}
                  </td>
                  {showActions && isAdmin && (
                    <td className="px-4 py-2 text-sm">
                      <div className="flex space-x-2">
                        {grn.status === 'draft' ? (
                          <>
                            <button
                              onClick={() => onEdit(grn)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              title="Edit GRN"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onFinalize(grn)}
                              className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                              title="Finalize GRN"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(grn)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete GRN"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => onView(grn)}
                              className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                              title="View GRN"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onEdit(grn)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              title="Edit GRN"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(grn)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete GRN"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {grns.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-700 border-t-2 border-gray-200 dark:border-gray-600">
                  <td colSpan={showActions ? 6 : 5} className="px-4 py-3 text-sm font-medium">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">
                        Total GRNs: {grns.length} | 
                        <span className="ml-2">
                          Status: {grns.filter(g => g.status === 'finalized').length} finalized, {grns.filter(g => g.status === 'draft').length} draft
                        </span>
                      </span>
                      <div className="flex items-center space-x-4">
                        <span className="text-gray-600 dark:text-gray-400">Balance:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          calculateTotalBalance() > 0 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' 
                            : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        }`}>
                          {calculateTotalBalance()} items remaining
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemType="GRN"
        itemName={deleteModal.grn?.grn_number || 'Draft'}
      />
    </div>
  );
}
